import React from "react";
import { Link } from "react-router-dom";
import {
  HardHat,
  Wrench,
  Building,
  ClipboardCheck,
  ShieldCheck,
  Zap,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const services = [
  {
    icon: Building,
    title: "Commercial Construction",
    description:
      "Full electrical & cable rough-in for new commercial builds — offices, retail, hospitality and industrial facilities.",
  },
  {
    icon: HardHat,
    title: "Residential Wiring",
    description:
      "Licensed residential electrical contracting — new homes, ADUs, renovations and service upgrades across Oʻahu.",
  },
  {
    icon: Wrench,
    title: "Renovations & Retrofits",
    description:
      "Rewiring older structures, panel upgrades, and modernizing infrastructure to current code and energy standards.",
  },
  {
    icon: Zap,
    title: "Power & Lighting Installs",
    description:
      "Service entrances, sub-panels, lighting circuits, EV chargers and backup generator installs done clean.",
  },
  {
    icon: ClipboardCheck,
    title: "Project Management",
    description:
      "Single point of accountability — permits, scheduling, subs, inspections and final sign-off handled end-to-end.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    description:
      "Fully licensed Hawaii contractor with general liability & workers' comp coverage on every project.",
  },
];

const credentials = [
  "Hawaii Licensed C-13 Electrical Contractor",
  "Fully bonded and insured",
  "NEC / NFPA 70 code compliant",
  "25+ years on the islands",
];

const BuildingContractor = () => {
  return (
    <section className="relative py-24 overflow-hidden" style={{ backgroundColor: "#f7faf8" }}>
      {/* ambient blob */}
      <div
        className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-10 pointer-events-none"
        style={{
          background: "radial-gradient(circle, #10b981 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6">
        {/* Heading */}
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-end mb-14">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Building Contractor
            </span>
            <h2
              className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1]"
              style={{ color: "#0a2e1f" }}
            >
              Full-service electrical contracting <br className="hidden md:block" />
              across <span className="text-emerald-700">Hawaii</span>
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed lg:pb-2">
            Beyond wire & cable, D-H Gadiola is a licensed building contractor
            delivering complete electrical installations for residential,
            commercial and industrial projects. From permit to punch-list, we
            run the job — so you don't have to.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {services.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-3xl p-8 bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
              >
                <div
                  className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                  style={{
                    background:
                      "radial-gradient(circle, #10b981, transparent 70%)",
                  }}
                />
                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30"
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    }}
                  >
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3
                    className="text-lg font-bold mb-2"
                    style={{ color: "#0a2e1f" }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {s.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Credentials band */}
        <div
          className="relative overflow-hidden rounded-[2rem] p-10 md:p-12 grid md:grid-cols-[1.4fr_1fr] gap-10 items-center"
          style={{
            background:
              "linear-gradient(135deg, #0a2e1f 0%, #0f5132 50%, #047857 100%)",
          }}
        >
          <div
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, #34d399 0%, transparent 70%)",
            }}
          />
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30"
                style={{
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                }}
              >
                <HardHat className="text-white" size={22} />
              </div>
              <span className="text-emerald-200 text-xs font-bold uppercase tracking-[0.25em]">
                Credentials
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 tracking-tight leading-tight">
              A licensed contractor you can actually get on the phone.
            </h3>
            <ul className="grid sm:grid-cols-2 gap-3 mb-8">
              {credentials.map((c, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-emerald-100/90"
                >
                  <CheckCircle2 size={17} className="text-emerald-300 mt-0.5 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/request-info"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-emerald-800 text-sm font-semibold shadow-xl hover:scale-[1.03] transition-transform"
              >
                Request a Quote <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm font-semibold border border-white/30 hover:bg-white/10 backdrop-blur-sm transition-colors"
              >
                Talk to Us
              </Link>
            </div>
          </div>
          <div className="relative hidden md:block">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl shadow-emerald-500/20">
              <img
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Contractor at work"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a2e1f]/30 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuildingContractor;
